import { html, nothing } from "lit";
import type { AppViewState } from "./app-view-state.ts";
import { hintForTab } from "./control-hints.ts";
import { icons } from "./icons.ts";
import { iconForTab, pathForTab, titleForTab, type Tab } from "./navigation.ts";
import type { ThemeTransitionContext } from "./theme-transition.ts";
import type { ThemeMode } from "./theme.ts";
import type { SessionsListResult } from "./types.ts";
import { renderFleetMultiAgentSwitcher } from "./views/fleet-multiagent-switcher.ts";

export function renderTab(state: AppViewState, tab: Tab) {
  const href = pathForTab(tab, state.basePath);
  return html`
    <a
      href=${href}
      class="nav-item ${state.tab === tab ? "active" : ""}"
      @click=${(event: MouseEvent) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        state.mobileNavMoreOpen = false;
        state.setTab(tab);
      }}
      title=${hintForTab(tab)}
      aria-label=${`${titleForTab(tab)}: ${hintForTab(tab)}`}
    >
      <span class="nav-item__icon" aria-hidden="true">${icons[iconForTab(tab)]}</span>
      <span class="nav-item__text">${titleForTab(tab)}</span>
    </a>
  `;
}

function toggleShellFullscreen(): void {
  const shell = document.querySelector(".shell");
  if (!shell) {
    return;
  }
  if (!document.fullscreenElement) {
    void shell.requestFullscreen?.();
  } else {
    void document.exitFullscreen?.();
  }
}

/**
 * @deprecated All controls moved to the topbar (JAISIU-3044). This helper
 * stays exported so any external import continues to type-check, but it
 * renders nothing. Use the dedicated helpers (`renderTopbarInspectorToggle`,
 * `renderFleetMultiAgentButton`, etc.) instead.
 */
export function renderChatControls(_state: AppViewState) {
  return nothing;
}

/**
 * Topbar Inspector toggle (JAISIU-3044).
 *
 * Replaces the cluster of controls that used to live in
 * `.content-header > .page-meta` for the chat tab. Only the Inspector
 * panel survives; it is promoted to the topbar, far right, next to the
 * theme toggle. Always visible (per operator decision 2026-09-02).
 *
 * Only acts on presets where an Inspector panel exists (`unified` and
 * `operator`). On `monitor` / `commandCenter` the click is a no-op; the
 * tooltip explains why so the affordance is not misleading.
 */
export function renderTopbarInspectorToggle(state: AppViewState) {
  const preset = state.settings.fleetWorkspacePreset;
  const inspectorExists = preset === "unified" || preset === "operator";
  const drawerOpen = state.settings.fleetUnifiedDrawerOpen !== false;
  const label = "Inspector";
  // Tooltip + aria-label carry the verb form ("Hide / Show Inspector
  // panel") for screen readers and hover. The visible label is just
  // "Inspector".
  const tooltip = inspectorExists
    ? drawerOpen
      ? "Hide Inspector panel"
      : "Show Inspector panel"
    : `Inspector panel is only present in the Unified and Operator presets (current: ${preset})`;

  // The simplest possible toggle: click → flip drawer-open. Nothing
  // else. No closure capture. No tab switch. Just the button.
  // `state.applySettings` mutates `host.settings` (a `@state` field),
  // which triggers Lit's reactivity and re-renders the host — including
  // the chat view's right column. Earlier iterations of this handler
  // appended an explicit `requestUpdate()` + `void host.updateComplete`
  // as a "force a synchronous re-render" safety net; that pair was both
  // redundant (Lit already re-renders on the `@state` write) and broken
  // (`void` discards the promise — it does not `await`), and it tripped
  // the "change-in-update" warning by scheduling a second update after
  // the one `applySettings` had already scheduled. The real fix for the
  // post-session-switch visual stall was in chat.ts (JAISIU-3603
  // followup — replacing `live(sidePanelOpen)` with a plain const), so
  // the safety net here is no longer needed.
  return html`
    <button
      type="button"
      class="topbar-inspector-toggle"
      data-testid="fleet-inspector-toggle"
      title=${tooltip}
      aria-label=${tooltip}
      aria-pressed=${drawerOpen}
      @click=${() => {
        if (!inspectorExists) return;
        state.applySettings({
          ...state.settings,
          fleetUnifiedDrawerOpen: !state.settings.fleetUnifiedDrawerOpen,
        });
      }}
    >
      <span aria-hidden="true">${drawerOpen ? "◧" : "◨"}</span>
      <span class="topbar-inspector-toggle__label">${label}</span>
    </button>
  `;
}

function resolveSessionDisplayName(key: string, row?: SessionsListResult["sessions"][number]) {
  const label = row?.label?.trim() || "";
  const displayName = row?.displayName?.trim() || "";
  if (label && label !== key) {
    return `${label} (${key})`;
  }
  if (displayName && displayName !== key) {
    return `${key} (${displayName})`;
  }
  return key;
}

function resolveSessionOptions(
  sessionKey: string,
  sessions: SessionsListResult | null,
  mainSessionKey?: string | null,
) {
  const seen = new Set<string>();
  const options: Array<{ key: string; displayName?: string }> = [];

  const resolvedMain = mainSessionKey && sessions?.sessions?.find((s) => s.key === mainSessionKey);
  const resolvedCurrent = sessions?.sessions?.find((s) => s.key === sessionKey);

  // Add main session key first
  if (mainSessionKey) {
    seen.add(mainSessionKey);
    options.push({
      key: mainSessionKey,
      displayName: resolveSessionDisplayName(mainSessionKey, resolvedMain || undefined),
    });
  }

  // Add current session key next
  if (!seen.has(sessionKey)) {
    seen.add(sessionKey);
    options.push({
      key: sessionKey,
      displayName: resolveSessionDisplayName(sessionKey, resolvedCurrent),
    });
  }

  // Add sessions from the result
  if (sessions?.sessions) {
    for (const s of sessions.sessions) {
      if (!seen.has(s.key)) {
        seen.add(s.key);
        options.push({
          key: s.key,
          displayName: resolveSessionDisplayName(s.key, s),
        });
      }
    }
  }

  return options;
}

const THEME_ORDER: ThemeMode[] = ["system", "light", "dark"];

export function renderThemeToggle(state: AppViewState) {
  // Light/dark-only toggle. The indicator follows themeResolved so
  // it lands on whichever button matches the page the operator is
  // actually looking at. "system" resolves to either light or dark
  // based on the OS preference, so it doesn't get its own slot.
  const lightDarkIndex = state.themeResolved === "dark" ? 1 : 0;
  const index = lightDarkIndex;
  const applyTheme = (next: ThemeMode) => (event: MouseEvent) => {
    const element = event.currentTarget as HTMLElement;
    const context: ThemeTransitionContext = { element };
    if (event.clientX || event.clientY) {
      context.pointerClientX = event.clientX;
      context.pointerClientY = event.clientY;
    }
    state.setTheme(next, context);
  };

  // JAISIU-3603 — operator trimmed the theme toggle to two options
  // (Light / Dark). The "match OS preference" choice lived in the
  // topbar's old location but was rarely used; the nav-bottom pill
  // needs to stay narrow so the bookend affordance never breaks the
  // sidebar layout.
  return html`
    <div class="theme-toggle theme-toggle--light-dark" style="--theme-index: ${index};">
      <div class="theme-toggle__track" role="group" aria-label="Theme">
        <span class="theme-toggle__indicator"></span>
        <button
          class="theme-toggle__button ${state.themeResolved === "light" ? "active" : ""}"
          @click=${applyTheme("light")}
          aria-pressed=${state.themeResolved === "light"}
          aria-label="Light theme"
          title="Theme: always use light colors"
        >
          ${renderSunIcon()}
        </button>
        <button
          class="theme-toggle__button ${state.themeResolved === "dark" ? "active" : ""}"
          @click=${applyTheme("dark")}
          aria-pressed=${state.themeResolved === "dark"}
          aria-label="Dark theme"
          title="Theme: always use dark colors"
        >
          ${renderMoonIcon()}
        </button>
      </div>
    </div>
  `;
}

function renderSunIcon() {
  return html`
    <svg class="theme-icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="4"></circle>
      <path d="M12 2v2"></path>
      <path d="M12 20v2"></path>
      <path d="m4.93 4.93 1.41 1.41"></path>
      <path d="m17.66 17.66 1.41 1.41"></path>
      <path d="M2 12h2"></path>
      <path d="M20 12h2"></path>
      <path d="m6.34 17.66-1.41 1.41"></path>
      <path d="m19.07 4.93-1.41 1.41"></path>
    </svg>
  `;
}

function renderMoonIcon() {
  return html`
    <svg class="theme-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401"
      ></path>
    </svg>
  `;
}

// ─── Multi-agent profile quick-switcher ───────────────────────────────────────

interface FleetMultiAgentHost {
  connected: boolean;
  fleetProfiles: import("./types.js").ExecutionProfile[];
  fleetProfilesLoading: boolean;
  fleetProfilesError: string | null;
  fleetMultiAgentSwitcherOpen: boolean;
  openFleetMultiAgentSwitcher: () => void;
  closeFleetMultiAgentSwitcher: () => void;
  switchFleetProfile: (profileId: string) => void;
  toggleFleetMultiAgent: (enabled: boolean) => void;
  setTab: (tab: import("./navigation.js").Tab) => void;
}

/** Active multi-agent icon SVG (filled version for the button). */
function multiAgentActiveIcon() {
  return html`
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  `;
}

/** Single-agent icon SVG (for inactive button). */
function multiAgentInactiveIcon() {
  return html`
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  `;
}

export function renderFleetMultiAgentButton(host: FleetMultiAgentHost) {
  const profiles = host.fleetProfiles ?? [];
  const activeProfile = profiles.find((p) => p.isDefault || profiles.length === 0);
  const isMultiAgent = activeProfile?.isMultiAgent ?? false;
  const isOpen = host.fleetMultiAgentSwitcherOpen ?? false;

  return html`
    <div style="position: relative; display: inline-flex;">
      <button
        type="button"
        class="fleet-icon-btn ${isMultiAgent ? "fleet-icon-btn--multiagent-active" : ""} ${isOpen ? "active" : ""}"
        ?disabled=${!host.connected}
        title=${isMultiAgent ? "Multi-agent active — click to manage (Ctrl+M)" : "Switch to multi-agent mode (Ctrl+M)"}
        aria-label="Multi-agent profile switcher"
        aria-expanded=${isOpen}
        aria-haspopup="true"
        @click=${() => {
          if (isOpen) {
            host.closeFleetMultiAgentSwitcher();
          } else {
            host.openFleetMultiAgentSwitcher();
          }
        }}
      >
        ${isMultiAgent ? multiAgentActiveIcon() : multiAgentInactiveIcon()}
        <span
          style="margin-left: 5px; font-size: 12px; font-weight: 500; white-space: nowrap; overflow: hidden; max-width: 80px; text-overflow: ellipsis;"
        >
          ${activeProfile?.name ?? "Profile"}
        </span>
      </button>

      <!-- Popover (positioned relative to the button wrapper) -->
      ${renderFleetMultiAgentSwitcher({
        open: isOpen,
        profiles,
        activeProfileId: activeProfile?.id ?? null,
        loading: host.fleetProfilesLoading,
        error: host.fleetProfilesError,
        onSwitchProfile: (profileId) => host.switchFleetProfile(profileId),
        onToggleMultiAgent: (enabled) => host.toggleFleetMultiAgent(enabled),
        onClose: () => host.closeFleetMultiAgentSwitcher(),
        onOpenAgentsPage: () => host.setTab("agents"),
      })}
    </div>
  `;
}
